import INTERVIEW_STATUS, { 
  getMenteeStatusLabel,
  INTERVIEW_STATUS_MENTOR,
  InterviewStatusValues,
} from 'enums/InterviewStatus';
import { FC } from 'react';
import styled from 'styled-components';
import Interview from 'types/Interview';

// --- Styles ---
const StyledChip = styled.span<{ color: string; bgColor: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ color }) => color};
  background-color: ${({ bgColor }) => bgColor};
`;

// --- Types ---
type Props = {
  interview: Interview & { isAuthor?: boolean }; 
  variant: 'mentee' | 'mentor' | 'admin';
  currentUserId?: number;
};

// --- Logique des couleurs ---
const getChipColors = (
  status: InterviewStatusValues,
): { color: string; bgColor: string } => {
  switch (status) {
    case 'WAITING_MENTOR':
      return { color: '#344054', bgColor: '#F2F4F7' };
    case 'WAITING_MENTEE':
      return { color: '#344054', bgColor: '#F2F4F7' };
    case 'FINISHED':
      return { color: '#027A48', bgColor: '#ECFDF3' };
    case 'CANCELLED':
    case 'NO_ANSWER':
    case 'REFUSED':
      return { color: '#B42318', bgColor: '#FEF3F2' };
    case 'ACCEPTED':
      return { color: '#175CD3', bgColor: '#EFF8FF' };
    default:
      return { color: '#344054', bgColor: '#F2F4F7' };
  }
};

const InterviewStatusChip: FC<Props> = ({ interview, variant, currentUserId }) => {
  
  // Récupération de l'ID via le token en dernier recours
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken'); 
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      return payload.id || payload.sub || payload.userId || payload.user?.id;
    } catch (e) {
      return null;
    }
  };

  const effectiveUserId = currentUserId || getUserIdFromToken();
  type StatusKey = keyof typeof INTERVIEW_STATUS;

  // Identification de l'auteur (comparaison robuste par nombre)
  const isAuthor = (interview as any).isAuthor === true || 
    (!!effectiveUserId && !!interview.authorId && Number(effectiveUserId) === Number(interview.authorId));

  const getLabel = () => {
    const status = interview.status as StatusKey;

    if (variant === 'mentee') {
      // Si Alex est l'auteur, on neutralise les messages directifs pour les entretiens en cours
      if (isAuthor && (status === 'WAITING_MENTEE' || status === 'WAITING_MENTOR')) {
        return 'En attente';
      }
      return getMenteeStatusLabel(status as any, isAuthor); 
    }
  
    if (variant === 'mentor') {
      // Même logique pour le mentor s'il est l'auteur
      if (isAuthor && (status === 'WAITING_MENTOR' || status === 'WAITING_MENTEE')) {
        return 'En attente';
      }
      return INTERVIEW_STATUS_MENTOR[status as keyof typeof INTERVIEW_STATUS_MENTOR] || status;
    }

    return INTERVIEW_STATUS[status] || status;
  };

  const colors = getChipColors(interview.status as any);
  const label = getLabel();

  return (
    <StyledChip color={colors.color} bgColor={colors.bgColor}>
      {label}
    </StyledChip>
  );
};

export default InterviewStatusChip;